import React, { useState } from 'react';
import { X, Send, Loader2, CheckCircle2, User, Mail, Phone, Building2, MessageSquare } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RequestSetupModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/setup-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      // Fallback to mailto if API fails
      const mailtoLink = `mailto:tradeofficeai@gmail.com?subject=2Tap Setup Request from ${formData.name}&body=Name: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0APhone: ${formData.phone || 'Not provided'}%0D%0ABusiness: ${formData.businessName || 'Not provided'}%0D%0A%0D%0AMessage:%0D%0A${formData.message || 'I would like to request a 2Tap setup.'}`;
      window.location.href = mailtoLink;
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({ name: '', email: '', phone: '', businessName: '', message: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#1F2A37] rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Get Me Started
          </h2>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="close-setup-modal"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {submitted ? (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Request Sent!</h3>
              <p className="text-slate-400 mb-6">
                Thanks for reaching out. We'll be in touch shortly to understand your workflow, tailor 2Tap for you, and discuss pricing options.
              </p>
              <Button 
                onClick={handleClose}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="setup-success-close"
              >
                Done
              </Button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-slate-400 text-sm mb-4">
                Share your details and we'll get in touch to understand your needs and how to tailor 2Tap for you.
              </p>

              {/* Name */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-orange-400" />
                  Your Name *
                </Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                  data-testid="setup-name-input"
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-orange-400" />
                  Email Address *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@company.com"
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                  data-testid="setup-email-input"
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-orange-400" />
                  Phone Number
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0400 000 000"
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                  data-testid="setup-phone-input"
                />
              </div>

              {/* Business Name */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-orange-400" />
                  Business Name
                </Label>
                <Input
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  placeholder="Smith Electrical Pty Ltd"
                  className="bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 focus:border-orange-500"
                  data-testid="setup-business-input"
                />
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-orange-400" />
                  Message (Optional)
                </Label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your needs..."
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 text-white placeholder:text-slate-500 rounded-md focus:outline-none focus:border-orange-500 resize-none"
                  data-testid="setup-message-input"
                />
              </div>

              {/* Submit Button */}
              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-5 font-bold tracking-wide mt-2"
                data-testid="setup-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Request
                  </>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-3">
                We typically respond within 24 hours.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestSetupModal;
